import { computed } from "@vue/composition-api";
import { SharedState } from "@/common/state";

/**
 * Visual tokens for the Gen AI Governance dashboard.
 *
 * This dashboard follows its own reference design (the one Guy signed off on),
 * which uses a lighter, cooler palette than the rest of the app. The project's
 * Tailwind config REPLACES the default palette (`theme.colors`, not
 * `theme.extend.colors`), so slate/blue/emerald utilities do not exist here —
 * these are applied as inline styles instead.
 *
 * The app switches themes by rewriting CSS variables on documentElement
 * (see `src/theme.ts`), which inline styles cannot react to. So both palettes
 * live here and `useGenAiPalette()` picks one reactively off SharedState.theme.
 * Scoped to this source only.
 *
 * Every foreground here is checked against the surface it actually lands on —
 * including the matrix's translucent stripe rows and the tinted severity chips,
 * which are composited, not flat. Text clears 4.5:1. If you change a value,
 * recheck it against its real background rather than against white.
 *
 * One honest exception, inherited rather than introduced: the severity DOTS do
 * not clear 3:1 in light. They are decorative repeats of a level that is always
 * also named in text next to them, so nothing depends on the dot alone - but
 * do not reuse a `dot` value as a text colour. Use `chipText` for that; the
 * slider's active stop label learned this the hard way (#EAB308 measured 1.85:1
 * on the panel).
 */

/** one severity tone; the map is indexed by the TONE_BY_LEVEL string */
export interface SeverityTone {
  dot: string;
  /** a flat fill, applied as `background` (gen-ai-ui draws its bars solid) */
  bar: string;
  chipBg: string;
  chipText: string;
}

/**
 * Risk severity, from gen-ai-ui `visualTokens.SEVERITY` (Sep 2026): red, orange
 * and yellow — never green. `none` is no risk and stays off every Risks card.
 *
 * `chipText` is the one place this departs from the prototype: its active
 * slider labels are orange-500 and yellow-600, which measure 2.8:1 and 2.9:1
 * as 10px text. The 700s of the same hues are used instead, so the label still
 * reads as its level and clears 4.5:1.
 */
const LIGHT_SEVERITY: Record<string, SeverityTone> = {
    high: { dot: "#E11D2E", bar: "#E11D2E", chipBg: "#FDE8EA", chipText: "#B91C1C" },
    medium: { dot: "#F97316", bar: "#F97316", chipBg: "#FFF1E6", chipText: "#C2410C" },
    low: { dot: "#EAB308", bar: "#EAB308", chipBg: "#FEF9C3", chipText: "#A16207" },
    none: { dot: "#94A3B8", bar: "#CBD5E1", chipBg: "#F1F5F9", chipText: "#475569" },
};

/**
 * Data sensitivity is a different scale from severity and has its own colour
 * language, so a reader can tell a classification card from a risk card at a
 * glance. From gen-ai-ui `visualTokens.CLASSIFICATION`: High is purple
 * #800080, Medium ("Notice") is pure blue #0000FF, Low is sky, Unclassified is
 * slate — the Purview label colours.
 *
 * Low's text steps down to sky-700: #0EA5E9 is 2.8:1 as text.
 */
const LIGHT_SENSITIVITY: Record<string, SeverityTone> = {
    high: { dot: "#800080", bar: "#800080", chipBg: "#F5E6F5", chipText: "#800080" },
    medium: { dot: "#0000FF", bar: "#0000FF", chipBg: "#E6E6FF", chipText: "#0000FF" },
    low: { dot: "#0EA5E9", bar: "#0EA5E9", chipBg: "#E0F2FE", chipText: "#0369A1" },
    none: { dot: "#94A3B8", bar: "#94A3B8", chipBg: "#F1F5F9", chipText: "#1E293B" },
};

/**
 * The prototype has no dark theme. These keep its hues but lift them until
 * they read on the dark card: #800080 and #0000FF are ~1.6:1 against #1D2C3D,
 * so purple and blue move to their 400s.
 */
const DARK_SENSITIVITY: Record<string, SeverityTone> = {
    high: { dot: "#C084FC", bar: "#C084FC", chipBg: "rgba(192,132,252,.16)", chipText: "#D8B4FE" },
    medium: { dot: "#6D7CFF", bar: "#6D7CFF", chipBg: "rgba(109,124,255,.18)", chipText: "#A5B4FC" },
    low: { dot: "#38BDF8", bar: "#38BDF8", chipBg: "rgba(56,189,248,.16)", chipText: "#7DD3FC" },
    none: { dot: "#94A3B8", bar: "#94A3B8", chipBg: "rgba(148,163,184,.18)", chipText: "#E2E8F0" },
};

const DARK_SEVERITY: Record<string, SeverityTone> = {
    high: { dot: "#F87171", bar: "#F87171", chipBg: "rgba(248,113,113,.16)", chipText: "#FCA5A5" },
    medium: { dot: "#FB923C", bar: "#FB923C", chipBg: "rgba(251,146,60,.16)", chipText: "#FDBA74" },
    low: { dot: "#FACC15", bar: "#FACC15", chipBg: "rgba(250,204,21,.16)", chipText: "#FDE047" },
    none: { dot: "#94A3B8", bar: "#64748B", chipBg: "rgba(148,163,184,.18)", chipText: "#CBD5E1" },
};

const LIGHT = {
  pageBg: "linear-gradient(135deg, #F0F2F7 0%, #E8ECF4 50%, #F0F2F7 100%)",
  /** translucent backdrop for the sticky filter bar */
  stickyBg: "rgba(240, 242, 247, 0.85)",
  card: {
    bg: "#FFFFFF",
    border: "rgba(226, 232, 240, 0.8)",
    shadow:
      "0 1px 2px 0 rgba(16,24,40,.03), 0 2px 8px -1px rgba(16,24,40,.05), 0 0 0 1px rgba(16,24,40,.01)",
    radius: "16px",
  },
  /**
   * gen-ai-ui's slate ramp. The prototype sets its footnotes, axis labels and
   * idle slider labels in slate-400, which is 2.6:1 on white; `muted` keeps
   * them at slate-500 (4.8:1) so every word on the page stays readable.
   */
  text: {
    /** headline numbers and the bold part of a title (slate-900) */
    title: "#0F172A",
    /** row counts and the rest of a title (slate-800) */
    value: "#1E293B",
    /** chip, pill, matrix-row and activity labels (slate-700) */
    strong: "#334155",
    /** category row labels (slate-600) */
    label: "#475569",
    muted: "#64748B",
    /** row glyphs (slate-500); redundant with the label beside them */
    icon: "#64748B",
    /** the quieter glyphs: chips, activity rows, count headers (slate-400) */
    iconSoft: "#94A3B8",
    /** the expand control — an interactive affordance, so it clears 4.5:1 */
    iconAction: "#475569",
  },
  /** what a clickable row does under the pointer (gen-ai-ui `group-hover:`) */
  hover: {
    /** the count, on risk and people rows (blue-600) */
    value: "#2563EB",
    /** the count, on the information row (purple-600) */
    valueInfo: "#9333EA",
    label: "#0F172A",
    icon: "#334155",
    iconSoft: "#475569",
  },
  /** keyboard focus ring; 5.2:1 against the card surface */
  focus: "#2563EB",
  /** hairline around the slider thumb */
  sliderRing: "rgba(15, 23, 42, 0.55)",
  /** empty part of every bar (slate-100) */
  track: "#F1F5F9",
  rowStripe: "rgba(248, 250, 252, 0.6)",
  divider: "#F1F5F9",
  cellHover: "#EEF2F8",
  cellHoverText: "#0F172A",
  /** donut ring separator — matches the card surface */
  ringBorder: "#FFFFFF",
  /** chart-view slice colours, from gen-ai-ui (Sep 2026) */
  categoryChart: {
    personal: "#6366F1",
    hr: "#8B5CF6",
    legal: "#0EA5E9",
    governance: "#14B8A6",
    business: "#3B82F6",
    financial: "#64748B",
  } as Record<string, string>,
  activityChart: {
    created: "#5C9BE8",
    modified: "#3E7BD6",
    exposed: "#9CC3F0",
    shared: "#8B5CF6",
    uploaded: "#6366F1",
  } as Record<string, string>,
  /** the boxed ruler panel on the slider cards (slate-50/80, slate-200/70) */
  panel: { bg: "rgba(248, 250, 252, 0.8)", border: "rgba(226, 232, 240, 0.7)" },
  /** the matrix card's lighter ruler (gen-ai-ui `compact`) */
  panelCompact: { bg: "#FFFFFF", border: "rgba(226, 232, 240, 0.8)" },
  /** the ruler's unfilled track (slate-200/70) */
  sliderTrack: "rgba(226, 232, 240, 0.7)",
  /** slider stops the selection has not reached yet (slate-300) */
  sliderNodeIdle: "#CBD5E1",
  /**
   * The ruler fill runs the whole scale inside the filled part of the track,
   * however short, as gen-ai-ui draws it. Its sensitivity gradient names a
   * blue via-stop too, but a later via- class overrides it there, so this is
   * the three-stop ramp that actually renders.
   */
  sliderFill: {
    severity: "linear-gradient(90deg, #E11D2E, #F97316, #EAB308)",
    sensitivity: "linear-gradient(90deg, #800080, #0EA5E9, #94A3B8)",
  } as Record<string, string>,
  /** the status pill naming the current stop */
  pill: { bg: "#FFFFFF", border: "rgba(226, 232, 240, 0.8)" },
  /** category filter chips on the AI agent card */
  chip: {
    onBg: "#316CC0",
    onBorder: "#316CC0",
    onText: "#FFFFFF",
    offBg: "rgba(248, 250, 252, 0.8)",
    offBorder: "rgba(226, 232, 240, 0.7)",
    offHoverBg: "rgba(241, 245, 249, 0.8)",
    offText: "#334155",
    offIcon: "#94A3B8",
  },
  /** the list/chart segmented control */
  toggle: {
    bg: "rgba(248, 250, 252, 0.8)",
    border: "#E2E8F0",
    onBg: "#FFFFFF",
    onBorder: "rgba(226, 232, 240, 0.8)",
    onIcon: "#1E293B",
    offIcon: "#94A3B8",
    offIconHover: "#475569",
  },
  /** the square expand button beside the toggle */
  action: { bg: "#FFFFFF", border: "#E2E8F0", borderHover: "#CBD5E1" },
  /** "N high-severity activities need review." (red-600, 4.8:1) */
  cta: { text: "#DC2626", hover: "#B91C1C" },
  /** donut legend rows under the pointer (slate-50) */
  legendHover: "#F8FAFC",
  /**
   * "AI Activities" cell chips (gen-ai-ui, Sep 2026): each row marks its
   * highest cell in soft yellow and its lowest in soft blue, and leaves the
   * rest plain. The legend swatch carries the border; the chips do not.
   * Text on both tints clears 4.5:1 (4.6 and 5.4).
   */
  matrixTone: {
    top: { bg: "#FFF8E1", text: "#A16207", border: "#F5E6B8" },
    bottom: { bg: "#EEF6FC", text: "#0369A1", border: "#BFDBFE" },
  },
  /**
   * The drill-down events modal and its details dialog (gen-ai-ui `Events`,
   * `EventsTable`, `EventDetailsModal`). They render in the app's modal
   * portal, outside the dashboard, so they size in px as the prototype does.
   * Secondary lines the prototype sets in slate-400 (2.6:1) use slate-500.
   */
  ev: {
    bg: "#FFFFFF",
    border: "rgba(226, 232, 240, 0.9)",
    shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    title: "#0F172A",
    text: "#1E293B",
    strong: "#334155",
    body: "#475569",
    muted: "#64748B",
    divider: "#F1F5F9",
    rowBg: "#FFFFFF",
    rowBorder: "rgba(226, 232, 240, 0.8)",
    rowHover: "rgba(248, 250, 252, 0.7)",
    headRule: "rgba(226, 232, 240, 0.9)",
    inputBg: "#FFFFFF",
    inputBorder: "#CBD5E1",
    inputBorderHover: "#94A3B8",
    inputDisabledBg: "#F8FAFC",
    lock: "#CBD5E1",
    menuShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
    menuHover: "#F8FAFC",
    menuActive: "#F1F5F9",
    iconBtn: "#94A3B8",
    iconBtnHover: "#334155",
    iconBtnHoverBg: "#F1F5F9",
    chipOffBg: "#F1F5F9",
    chipOffText: "#94A3B8",
    apply: "#1E293B",
    applyText: "#FFFFFF",
    primary: "#2563EB",
    primaryHover: "#1D4ED8",
    rangeFill: "#3B82F6",
    rangeTrack: "#E2E8F0",
    fieldBg: "#F8FAFC",
    fieldBorder: "#E2E8F0",
    badge: { bg: "#EFF6FF", text: "#2563EB", border: "#DBEAFE" },
    extra: { bg: "#F1F5F9", text: "#475569", border: "#E2E8F0" },
    card: { border: "#E2E8F0", divider: "#F1F5F9" },
    person: { bg: "#FFFFFF", border: "#E2E8F0" },
    investigate: { text: "#2563EB", border: "#BFDBFE", hoverBg: "#EFF6FF" },
  },
  severity: LIGHT_SEVERITY,
  sensitivity: LIGHT_SENSITIVITY,
};

/** dark values are drawn from the app's own dark ramp so the panel sits in the shell */
const DARK: typeof LIGHT = {
  pageBg: "linear-gradient(135deg, #1B2942 0%, #16223A 50%, #1B2942 100%)",
  stickyBg: "rgba(27, 41, 66, 0.85)",
  card: {
    bg: "#1D2C3D",
    border: "rgba(60, 81, 109, 0.7)",
    shadow:
      "0 1px 2px 0 rgba(0,0,0,.2), 0 2px 8px -1px rgba(0,0,0,.28), 0 0 0 1px rgba(255,255,255,.02)",
    radius: "16px",
  },
  text: {
    title: "#F5F7FB",
    value: "#E7ECF3",
    strong: "#D2DBE7",
    label: "#BAC7D8",
    muted: "#94A5BB",
    icon: "#8FA3BD",
    iconSoft: "#6C88AD",
    iconAction: "#BAC7D8",
  },
  hover: {
    value: "#7FB0E8",
    valueInfo: "#C084FC",
    label: "#F5F7FB",
    icon: "#D2DBE7",
    iconSoft: "#BAC7D8",
  },
  focus: "#7FB0E8",
  sliderRing: "rgba(245, 247, 251, 0.55)",
  track: "#26384D",
  rowStripe: "rgba(33, 54, 75, 0.55)",
  divider: "rgba(60, 81, 109, 0.5)",
  cellHover: "#273B52",
  cellHoverText: "#F5F7FB",
  ringBorder: "#1D2C3D",
  // one step lighter than light mode: mid-tone slices sink into a dark card
  categoryChart: {
    personal: "#818CF8",
    hr: "#A78BFA",
    legal: "#38BDF8",
    governance: "#2DD4BF",
    business: "#60A5FA",
    financial: "#94A3B8",
  },
  activityChart: {
    created: "#7FB0E8",
    modified: "#5C9BE8",
    exposed: "#B3D3F5",
    shared: "#A78BFA",
    uploaded: "#818CF8",
  },
  panel: { bg: "rgba(33, 54, 75, 0.45)", border: "rgba(60, 81, 109, 0.5)" },
  panelCompact: { bg: "#1D2C3D", border: "rgba(60, 81, 109, 0.6)" },
  sliderTrack: "rgba(60, 81, 109, 0.55)",
  sliderNodeIdle: "#41556E",
  sliderFill: {
    severity: "linear-gradient(90deg, #F87171, #FB923C, #FACC15)",
    sensitivity: "linear-gradient(90deg, #C084FC, #38BDF8, #94A3B8)",
  },
  pill: { bg: "#1D2C3D", border: "rgba(60, 81, 109, 0.6)" },
  chip: {
    onBg: "#4A8AE0",
    onBorder: "#4A8AE0",
    onText: "#0B1E33",
    offBg: "rgba(38, 56, 77, 0.7)",
    offBorder: "rgba(60, 81, 109, 0.5)",
    offHoverBg: "#2C4159",
    offText: "#D2DBE7",
    offIcon: "#6C88AD",
  },
  toggle: {
    bg: "rgba(33, 54, 75, 0.45)",
    border: "rgba(60, 81, 109, 0.6)",
    onBg: "#26384D",
    onBorder: "rgba(88, 110, 138, 0.7)",
    onIcon: "#F5F7FB",
    offIcon: "#6C88AD",
    offIconHover: "#BAC7D8",
  },
  action: { bg: "#1D2C3D", border: "rgba(60, 81, 109, 0.6)", borderHover: "#586E8A" },
  cta: { text: "#FCA5A5", hover: "#FECACA" },
  legendHover: "#243548",
  matrixTone: {
    top: {
      bg: "rgba(250, 204, 21, 0.14)",
      text: "#FDE047",
      border: "rgba(250, 204, 21, 0.3)",
    },
    bottom: {
      bg: "rgba(56, 189, 248, 0.14)",
      text: "#7DD3FC",
      border: "rgba(56, 189, 248, 0.3)",
    },
  },
  ev: {
    bg: "#1D2C3D",
    border: "rgba(60, 81, 109, 0.7)",
    shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    title: "#F5F7FB",
    text: "#E7ECF3",
    strong: "#D2DBE7",
    body: "#BAC7D8",
    muted: "#94A5BB",
    divider: "rgba(60, 81, 109, 0.5)",
    rowBg: "#1D2C3D",
    rowBorder: "rgba(60, 81, 109, 0.6)",
    rowHover: "rgba(38, 56, 77, 0.6)",
    headRule: "rgba(60, 81, 109, 0.7)",
    inputBg: "#1D2C3D",
    inputBorder: "rgba(88, 110, 138, 0.8)",
    inputBorderHover: "#8FA3BD",
    inputDisabledBg: "#243548",
    lock: "#6C88AD",
    menuShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.4)",
    menuHover: "#243548",
    menuActive: "#2C4159",
    iconBtn: "#8FA3BD",
    iconBtnHover: "#F5F7FB",
    iconBtnHoverBg: "#2C4159",
    chipOffBg: "#26384D",
    chipOffText: "#6C88AD",
    apply: "#E7ECF3",
    applyText: "#0B1E33",
    primary: "#4A8AE0",
    primaryHover: "#6FA3E8",
    rangeFill: "#4A8AE0",
    rangeTrack: "#26384D",
    fieldBg: "#243548",
    fieldBorder: "rgba(60, 81, 109, 0.7)",
    badge: { bg: "rgba(74, 138, 224, 0.16)", text: "#9CC3F0", border: "rgba(74, 138, 224, 0.35)" },
    extra: { bg: "#26384D", text: "#BAC7D8", border: "rgba(88, 110, 138, 0.6)" },
    card: { border: "rgba(60, 81, 109, 0.7)", divider: "rgba(60, 81, 109, 0.5)" },
    person: { bg: "#1D2C3D", border: "rgba(60, 81, 109, 0.7)" },
    investigate: { text: "#9CC3F0", border: "rgba(74, 138, 224, 0.45)", hoverBg: "rgba(74, 138, 224, 0.16)" },
  },
  severity: DARK_SEVERITY,
  sensitivity: DARK_SENSITIVITY,
};

export const is_dark = () => SharedState.theme.value === "dark";

/** reactive palette — use this in components so theme switches repaint */
export const useGenAiPalette = () =>
  computed(() => (is_dark() ? DARK : LIGHT));

/**
 * ── Sizing ────────────────────────────────────────────────────────────────
 *
 * This scale used to be authored in `rem`, on the assumption that rem tracks
 * the app's root sizing. It does — but the root is not 16px. `main.scss` sets
 * `html { font-size: 85% }` and steps it down to 75% under 1800px, 62.5% under
 * 1280px and 55% under 1200px. So 1rem is 12px on a 1440 screen and 10px on a
 * 1280 one, and the old scale rendered the card descriptions and matrix axis
 * labels at 8.25px / 6.9px — under a 27px page heading. Everything on the
 * dashboard landed between 8px and 13px, which is why it read as one flat grey
 * field with nothing leading.
 *
 * So the dashboard carries its own unit instead. `UNIT` is set as `--ga-u` on
 * the dashboard root and every size below is a multiple of it, which keeps the
 * type, the icons, the bars and the gutters locked in proportion at any width
 * — the fixed-px icons and donuts used to drift out of scale as the root
 * shrank. The unit is clamped, so the dashboard stops collapsing with the app
 * shell but still gives a little at narrow widths.
 *
 * The fallback in `u()` matters: the drill-down modals render through the
 * app's portal at the document root, outside this dashboard's subtree, so
 * `--ga-u` is not in scope there and they resolve to the fallback instead.
 *
 * Scoped to this source only — nothing here changes the rest of the app.
 */
export const UNIT = "clamp(14px, 0.55vw + 6.5px, 17px)";

const u = (n: number) => `calc(var(--ga-u, 15px) * ${n})`;

/** numerals are tabular so digit columns line up down a card and across matrix
 *  rows — proportional figures make 86/41/29 wobble */
const NUM = {
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum" 1',
  letterSpacing: "-0.01em",
};

/**
 * The type scale, from gen-ai-ui's px sizes (Sep 2026). Each is written as a
 * multiple of the unit that lands on the prototype's size at a 1440px screen,
 * where `--ga-u` resolves to ~14.4px — so 15px is `u(1.04)` and 36px `u(2.5)`.
 */
export const TYPE = {
  /** card title (15px semibold, tight). The prototype sets it in Inter with
   *  tracking-tighter; Roboto is narrower, so it is pulled in a little less */
  title: {
    fontSize: u(1.04),
    fontWeight: 600,
    letterSpacing: "-0.035em",
    wordSpacing: "-0.08em",
  },
  /** the label naming a group of cards ("Risks", "Visibility") */
  sectionTitle: {
    fontSize: u(0.9),
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  /** the headline number beside each card title (36px) */
  numHero: {
    ...NUM,
    fontSize: u(2.5),
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "-0.025em",
  },
  /** a category row's count (15px bold) */
  numRow: { fontSize: u(1.04), fontWeight: 700, lineHeight: 1, ...NUM },
  /** an activity row's count on the AI agent card (14px bold) */
  numRowSm: { fontSize: u(0.97), fontWeight: 700, ...NUM },
  /** a donut legend's share (15px bold) */
  numLegend: { fontSize: u(1.04), fontWeight: 700, lineHeight: 1, ...NUM },
  /** the modals' counts */
  numLg: { fontSize: u(1.2), fontWeight: 700, ...NUM },
  /** an activity row's share of the card ("38%", 12px) */
  pct: { fontSize: u(0.83), fontWeight: 500, ...NUM },
  /** category row labels (12.5px) */
  rowLabel: { fontSize: u(0.87), fontWeight: 500 },
  /** activity row labels and the severity legend (11px) */
  small: { fontSize: u(0.76), fontWeight: 500 },
  /** the card's share line under the title (11px) */
  subtitle: { fontSize: u(0.76), fontWeight: 500 },
  /** slider stop labels and the status pill (10.5px) */
  tick: { fontSize: u(0.73), fontWeight: 500 },
  /** category filter chip labels (11.5px) */
  chipLabel: { fontSize: u(0.8), fontWeight: 600 },
  /** matrix cells (11px) */
  cell: { fontSize: u(0.76), fontWeight: 500, ...NUM },
  /** matrix row labels, its column caption and legend (10px) */
  axis: { fontSize: u(0.69), fontWeight: 500 },
  /** donut legend labels (10.5px) */
  legend: { fontSize: u(0.73), fontWeight: 400, lineHeight: 1.25 },
  /** the review call-to-action under the AI agent card (11.5px) */
  cta: { fontSize: u(0.8), fontWeight: 600 },
  /** card footnotes (11px) */
  caption: { fontSize: u(0.76), fontWeight: 400 },
  /** the modals' row labels */
  label: { fontSize: u(0.92), fontWeight: 500 },
  /** tabular chip text, kept for the modals */
  chip: { fontSize: u(0.78), fontWeight: 500, ...NUM },
};

/**
 * Geometry, in the same unit as the type, from gen-ai-ui's Tailwind classes
 * (the px each resolves to at a 1440px screen is noted where it helps).
 */
export const SIZE = {
  /** space between cards (gap-5, 20px) */
  gutter: u(1.4),
  /** the dashboard's side padding; the sticky bar pulls out by the same */
  pagePad: u(1.66),
  pagePadNeg: u(-1.66),
  /** air between the two card groups (mb-8) and under their labels (mb-3.5) */
  sectionGap: u(2.2),
  sectionTitleGap: u(0.97),
  filterWidth: u(15),
  /** card shell: p-6, header mb-4, number-to-title gap-3 */
  cardPad: u(1.66),
  titleGap: u(1.1),
  heroGap: u(0.83),
  /** between header controls (gap-2.5) */
  actionGap: u(0.69),
  /** the list/chart segmented toggle and the expand button (w-7 h-7) */
  toggleBtn: u(1.94),
  togglePad: "2px",
  toggleRadius: u(0.55),
  toggleInnerRadius: u(0.42),
  actionBtn: u(1.94),
  iconSm: u(0.83),
  icon: u(0.97),
  /** footer: at least mt-6 above it, pt-3 inside it */
  footerGap: u(1.66),
  descPad: u(0.83),
  /** category rows: gap-2 inside a row, gap-3 between rows */
  rowGap: u(0.55),
  barListGap: u(0.83),
  /** activity rows (label above bar) are further apart (gap-7) */
  stackRowGap: u(1.94),
  /** the share column beside an activity count (w-8) */
  colPct: u(2.2),
  /** the severity legend: gap-x-3 gap-y-1.5, dots w-2, mb-5 below */
  legendGap: u(0.83),
  legendGapY: u(0.42),
  legendDot: u(0.55),
  severityGap: u(1.4),
  /** an activity row's label line sits mb-2 above its bar */
  tickGap: u(0.55),
  dot: u(0.55),
  /** category bars (h-2) and activity bars (h-2.5) */
  barStack: u(0.55),
  barRisk: u(0.69),
  /** chart view: a 150px ring, gap-4 to a legend of gap-2.5 rows */
  donutMd: u(10.4),
  donutGap: u(1.1),
  donutEmpty: u(12.5),
  legendRowGap: u(0.69),
  legendDotGap: u(0.55),
  legendDotNudge: u(0.28),
  legendMax: u(15.3),
  /** category row columns: count w-12, glyph w-4, label w-20 / w-24 */
  colCount: u(3.33),
  colIcon: u(1.1),
  colLabel: u(5.55),
  colLabelWide: u(6.66),
  /** category filter chips: gap-2 grid, gap-1.5 inside, py-1.5 px-2.5, mb-4 */
  chipGap: u(0.55),
  chipIconGap: u(0.42),
  chipPadding: `${u(0.42)} ${u(0.69)}`,
  chipsBottom: u(1.1),
  /** the ruler: p-3 (p-2.5 compact), pill row mb-2.5, mb-4 (mb-3) below */
  panelPad: u(0.83),
  panelPadCompact: u(0.69),
  pillRowGap: u(0.69),
  pillPad: `${u(0.14)} ${u(0.69)}`,
  pillGap: u(0.42),
  sliderTrack: u(0.55),
  trackInset: u(0.28),
  nodeSize: u(0.97),
  tickTop: u(0.69),
  rulerGap: u(1.1),
  rulerGapCompact: u(0.83),
  /** matrix: w-24 label column, min-w-[360px], w-6 icon chips, py-1.5 rows */
  matrixLabelCol: u(6.66),
  matrixCell: u(2.64),
  matrixMinWidth: u(25),
  matrixChip: u(1.66),
  matrixRowPad: u(0.42),
  matrixHeadPad: u(0.55),
  cellPad: `${u(0.14)} ${u(0.55)}`,
  cellMinWidth: u(2.5),
  /** matrix legend: w-3 swatches, gap-x-2.5, mb-2 */
  swatch: u(0.83),
  matrixLegendGap: u(0.69),
  matrixLegendBottom: u(0.55),
};
